import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Share2, Shield, AlertTriangle, Clock, Activity, MapPin, Tag, Globe, Zap } from 'lucide-react';
import { Alert, HazardType } from '../types';

interface HazardDetailOverlayProps {
    hazard: Alert | null;
    onClose: () => void;
    onShare?: (hazard: Alert) => void;
}

const HazardDetailOverlay: React.FC<HazardDetailOverlayProps> = ({ hazard, onClose, onShare }) => {
    if (!hazard) return null;

    const [isAskingAi, setIsAskingAi] = useState(false);
    const [imageError, setImageError] = useState(false); // NEW: Track image load error

    const handleAskAi = () => {
        setIsAskingAi(true);
        setTimeout(() => setIsAskingAi(false), 2000);
    };

    const severityColor =
        hazard.severity === 'critical' ? '#EF4444' :
            hazard.severity === 'high' ? '#D946EF' :
                hazard.severity === 'medium' ? '#F59E0B' : '#3B82F6';

    return (
        <AnimatePresence>
            <motion.div
                className="fixed inset-0 z-[110] flex items-end md:items-center justify-center pointer-events-none"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
            >
                <div className="absolute inset-0 bg-black/60 backdrop-blur-sm pointer-events-auto" onClick={onClose} />

                <motion.div
                    className="relative pointer-events-auto bg-[#0F172A] border border-red-500/20 w-full md:w-[600px] h-[85vh] md:h-auto md:max-h-[85vh] md:rounded-2xl rounded-t-3xl shadow-2xl flex flex-col overflow-hidden"
                    initial={{ y: "100%" }}
                    animate={{ y: 0 }}
                    exit={{ y: "100%" }}
                    transition={{ type: "spring", damping: 25, stiffness: 300 }}
                    style={{ borderColor: `${severityColor}40` }}
                >
                    {/* 1. HEADER: Source & Meta */}
                    <div className="flex items-center justify-between p-4 border-b border-white/5 bg-[#0b1021]">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center border border-red-500/30">
                                <Activity size={20} style={{ color: severityColor }} />
                            </div>
                            <div>
                                <h3 className="text-white font-bold text-sm leading-tight flex items-center gap-2">
                                    {hazard.source || 'CORTEX DETECT'}
                                    {hazard.isOfficial && <Shield size={12} className="text-blue-400" />}
                                </h3>
                                <div className="flex items-center gap-2 text-xs text-slate-400">
                                    <span style={{ color: severityColor }} className="font-bold">#{hazard.id}</span>
                                    <span>•</span>
                                    <span>{hazard.time || 'LIVE'}</span>
                                </div>
                            </div>
                        </div>
                        <button onClick={onClose} className="p-2 -mr-2 text-slate-400 hover:text-white">
                            <X size={24} />
                        </button>
                    </div>

                    <div className="overflow-y-auto custom-scrollbar flex-1 bg-[#0F172A]">
                        {/* 2. MEDIA & MAP - WITH SCANNING ANIMATION FALLBACK */}
                        <div className="relative aspect-video bg-[#020617] w-full overflow-hidden flex items-center justify-center group">
                            {(!hazard.imageUrl || imageError) ? (
                                // --- FALLBACK SCANNING ANIMATION ---
                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                    {/* Grid Background */}
                                    <div className="absolute inset-0 opacity-20"
                                        style={{ backgroundImage: 'linear-gradient(#1e293b 1px, transparent 1px), linear-gradient(90deg, #1e293b 1px, transparent 1px)', backgroundSize: '20px 20px' }}></div>

                                    {/* Radar Sweep */}
                                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[var(--neon-cyan)]/10 to-transparent opacity-50 animate-scan"></div>

                                    {/* Central Target */}
                                    <div className="relative z-10 flex flex-col items-center gap-3">
                                        <div className="relative w-16 h-16 border-2 border-[var(--neon-cyan)]/30 rounded-full flex items-center justify-center animate-pulse">
                                            <div className="w-12 h-12 border border-[var(--neon-cyan)] rounded-full animate-spin-slow"></div>
                                            <div className="absolute inset-0 border-t-2 border-[var(--neon-cyan)] rounded-full animate-spin"></div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-[var(--neon-cyan)] font-mono text-xs tracking-[0.2em] font-bold animate-pulse">ANALYZING TERRAIN</div>
                                            <div className="text-slate-500 font-mono text-[10px] mt-1">NO VISUAL LINK ESTABLISHED</div>
                                        </div>
                                    </div>

                                    {/* Corners */}
                                    <div className="absolute top-4 left-4 w-4 h-4 border-t-2 border-l-2 border-[var(--neon-cyan)]/50"></div>
                                    <div className="absolute top-4 right-4 w-4 h-4 border-t-2 border-r-2 border-[var(--neon-cyan)]/50"></div>
                                    <div className="absolute bottom-4 left-4 w-4 h-4 border-b-2 border-l-2 border-[var(--neon-cyan)]/50"></div>
                                    <div className="absolute bottom-4 right-4 w-4 h-4 border-b-2 border-r-2 border-[var(--neon-cyan)]/50"></div>
                                </div>
                            ) : (
                                // --- REAL IMAGE ---
                                <img
                                    src={hazard.imageUrl}
                                    alt={hazard.type}
                                    className="w-full h-full object-cover opacity-80"
                                    onError={() => setImageError(true)}
                                />
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] to-transparent pointer-events-none" />

                            {/* Severity Badge */}
                            <div className="absolute top-4 left-4 px-3 py-1 rounded bg-black/60 backdrop-blur border border-white/10 flex items-center gap-2 z-20">
                                <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: severityColor }} />
                                <span className="text-xs font-bold text-white uppercase tracking-wider">{hazard.severity} SEVERITY</span>
                            </div>

                            {/* Location HUD */}
                            <div className="absolute bottom-4 right-4 bg-black/60 backdrop-blur px-3 py-1.5 rounded-lg border border-white/10 flex items-center gap-2 text-[10px] font-mono text-blue-400 tracking-wider z-20">
                                <MapPin size={12} />
                                <span>{hazard.location}</span>
                            </div>
                        </div>

                        {/* 3. BODY */}
                        <div className="p-5">
                            <h2 className="text-2xl font-black text-white mb-2 font-[Rajdhani] uppercase tracking-wide">
                                {hazard.type} DETECTED
                            </h2>

                            <p className="text-slate-300 text-sm leading-relaxed mb-6 border-l-2 border-slate-700 pl-4">
                                {hazard.description || "Anomaly detected in this sector. Proceed with caution."}
                            </p>

                            {/* 4. TACTICAL ADVICE */}
                            <div className="mb-6 bg-red-500/5 border border-red-500/10 rounded-xl overflow-hidden">
                                <div className="bg-red-500/10 px-4 py-2 border-b border-red-500/10 flex items-center justify-between">
                                    <h4 className="text-xs font-bold text-red-400 uppercase tracking-widest flex items-center gap-2">
                                        <AlertTriangle size={12} /> PROTOCOL
                                    </h4>
                                </div>
                                <div className="p-4">
                                    <ul className="space-y-3">
                                        {(hazard.advice || ["Stay alert", "Monitor local feeds"]).map((tip, i) => (
                                            <li key={i} className="flex items-start gap-3 text-sm text-slate-300">
                                                <div className="mt-1 min-w-[16px] h-4 rounded-full bg-red-500/20 text-red-400 flex items-center justify-center text-[10px] font-bold">
                                                    {i + 1}
                                                </div>
                                                <span>{tip}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 5. FOOTER */}
                    <div className="p-4 border-t border-white/5 bg-[#0b1021] flex items-center gap-3">
                        <button
                            onClick={handleAskAi}
                            disabled={isAskingAi}
                            className="flex-1 py-3 bg-[var(--neon-cyan)] hover:brightness-110 text-black rounded-lg font-bold text-sm transition-all flex items-center justify-center gap-2"
                        >
                            {isAskingAi ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                                    Analyzing Threat...
                                </>
                            ) : (
                                <>
                                    <Zap size={16} />
                                    Ask AI Assistant
                                </>
                            )}
                        </button>
                        <button
                            onClick={() => onShare ? onShare(hazard) : null}
                            className="p-3 bg-white/5 hover:bg-white/10 text-white rounded-lg transition-colors"
                        >
                            <Share2 size={20} />
                        </button>
                    </div>

                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default HazardDetailOverlay;
